import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../Button";
import { Iconfont } from "../Iconfont";
import "./FirmwareUpdateView.scss";

/** 兼容星闪数组与单条字符串：`"说明"` / `["说明1","说明2"]` */
export function normalizeReleaseNotes(raw: unknown): string[] {
  if (raw == null) return [];
  if (typeof raw === "string") {
    const s = raw.trim();
    return s ? [s] : [];
  }
  if (Array.isArray(raw)) {
    return raw.map((n) => String(n ?? "").trim()).filter(Boolean);
  }
  return [];
}

/** 按字节数选择 KB / MB 展示 */
function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  }
  return (bytes / 1024).toFixed(1) + " KB";
}

/**
 * 固件 size 展示：
 * - number：按字节，自动用 KB / MB
 * - string：兼容 "776.8KB" / "1.2 MB" / "786.8KB" 等；纯数字按字节处理
 */
export const formatFileSize = (
  size: number | string | null | undefined
): string => {
  if (size == null) return "—";

  if (typeof size === "string") {
    const s = size.trim();
    if (!s) return "—";
    const labeled = /^(\d+(?:\.\d+)?)\s*(B|KB|MB|GB)$/i.exec(s);
    if (labeled) {
      return `${labeled[1]} ${labeled[2]!.toUpperCase()}`;
    }
    const asNum = Number(s);
    if (Number.isFinite(asNum) && asNum >= 0) {
      return formatBytes(asNum);
    }
    return "—";
  }

  if (!Number.isFinite(size) || size < 0) return "—";
  return formatBytes(size);
};

/** 去掉展示用前缀（如 `V.` / `v`），不参与版本逻辑 */
function firmwareVersionCore(version: string): string {
  return String(version || "")
    .trim()
    .replace(/^[Vv][.\s]*/, "")
    .trim();
}

/** 比较固件版本号，返回 v1 相对 v2：正数表示 v1 更新（忽略 UI 上的 V. 前缀） */
export const compareFirmwareVersions = (v1: string, v2: string): number => {
  const parts1 = firmwareVersionCore(v1).split(".").map(Number);
  const parts2 = firmwareVersionCore(v2).split(".").map(Number);

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const p1 = parts1[i] || 0;
    const p2 = parts2[i] || 0;
    if (p1 !== p2) return p1 - p2;
  }
  return 0;
};

export interface FirmwareUpdateTarget {
  version?: string;
  /** 字节 number，或 "776.8KB" 这类可读字符串 */
  size?: number | string | null;
  /** 单条字符串或字符串数组均可 */
  releaseNotes?: string | string[];
}

export interface FirmwareUpdateViewProps {
  currentVersion: string;
  targetFirmware: FirmwareUpdateTarget | null;
  isUpdating: boolean;
  updateStatus: string;
  updateStage: string;
  updateProgress: number;
  onUpdate: () => void;
  onClose: () => void;
  onRetry: () => void;
  translationPrefix?: string;
  actionsPrefix?: string;
  otherPrefix?: string;
  primaryActionLabel?: string;
  /** 固件库中最新版本号，用于判断是否展示「最新」标签 */
  latestCatalogVersion?: string;
}

export const FirmwareUpdateView: React.FC<FirmwareUpdateViewProps> = ({
  currentVersion,
  targetFirmware,
  isUpdating,
  updateStatus,
  updateStage,
  updateProgress,
  onUpdate,
  onClose,
  onRetry,
  translationPrefix = "keyboard.firmware",
  actionsPrefix = "keyboard.button",
  otherPrefix = "keyboard.other",
  primaryActionLabel,
  latestCatalogVersion,
}) => {
  const { t } = useTranslation();
  const tp = translationPrefix;
  const ap = actionsPrefix;
  const op = otherPrefix;

  const showProgress =
    isUpdating ||
    updateStatus === "downloading" ||
    updateStatus === "updating" ||
    updateStatus === "rebooting";
  const isRebooting = updateStatus === "rebooting";
  const showProgressBar = showProgress && !isRebooting;

  const progressPercent = Math.min(100, Math.max(0, updateProgress));

  const getProgressLabel = () => {
    if (isRebooting) {
      return updateStage || t(`${tp}.rebooting`);
    }
    if (updateStatus === "downloading") {
      return updateStage || t(`${tp}.loadingFirmware`);
    }
    return t(`${tp}.updatingProgress`, {
      progress: Math.round(progressPercent),
    });
  };

  const targetVersion = targetFirmware?.version ?? "";
  const compareToCurrent = compareFirmwareVersions(
    targetVersion,
    currentVersion || ""
  );
  const isUpgrade = compareToCurrent > 0;
  const showLatestBadge =
    !!latestCatalogVersion &&
    compareFirmwareVersions(targetVersion, latestCatalogVersion) === 0;
  const versionCardTitle = isUpgrade
    ? t(`${tp}.newVersionFound`, {
        size: formatFileSize(targetFirmware?.size),
      })
    : t(`${tp}.targetVersionFound`, {
        size: formatFileSize(targetFirmware?.size),
      });
  const releaseNotes = normalizeReleaseNotes(targetFirmware?.releaseNotes);

  return (
    <div className="firmware-update-modal">
      <div className="firmware-update-warning">
        <Iconfont icon="warning" className="firmware-update-warning-icon" />
        <span className="firmware-update-warning-text">
          {t(`${tp}.upgradeKeepConnected`)}
        </span>
      </div>

      <p className="firmware-current-version">
        {t(`${tp}.currentVersion`)}：{currentVersion || "-"}
      </p>

      {targetFirmware ? (
        <div className="firmware-new-version-card">
          <div className="firmware-new-version-header">
            <span className="firmware-new-version-title">
              {versionCardTitle}
            </span>
            <span className="firmware-new-version-meta">
              <span className="firmware-new-version-number">
                {targetFirmware.version || "-"}
              </span>
              {showLatestBadge ? (
                <span className="latest-badge">{t(`${op}.latest`)}</span>
              ) : null}
            </span>
          </div>
          {releaseNotes.length ? (
            <ul className="firmware-new-version-notes">
              {releaseNotes.map((note, idx) => (
                <li key={idx}>{note}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      <div className="firmware-update-actions">
        {updateStatus === "success" ? (
          <div className="success-icon">✓</div>
        ) : null}

        {updateStatus === "error" ? (
          <div className="error-message">
            <span className="error-icon">⚠</span>
            <span>{updateStage || t(`${tp}.updateFailed`)}</span>
          </div>
        ) : null}

        {showProgress ? (
          <div className="progress-container">
            {showProgressBar ? (
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            ) : null}
            <div className="progress-text">{getProgressLabel()}</div>
          </div>
        ) : null}

        {!isUpdating && !updateStatus && targetFirmware ? (
          <Button className="install-btn" variant="primary" onClick={onUpdate}>
            {primaryActionLabel ?? t(`${ap}.oneClickUpdate`)}
          </Button>
        ) : null}

        {updateStatus === "success" ? (
          <Button className="install-btn" variant="primary" onClick={onClose}>
            {t(`${ap}.complete`)}
          </Button>
        ) : null}

        {updateStatus === "error" ? (
          <Button className="install-btn" variant="primary" onClick={onRetry}>
            {t(`${ap}.retry`)}
          </Button>
        ) : null}
      </div>
    </div>
  );
};
